/**
 * Copyright (C) 2018–2020 HERE Global B.V. and its affiliate(s).
 * All rights reserved.
 *
 * This software and other materials contain proprietary information
 * controlled by HERE and are protected by applicable copyright legislation.
 * Any use and utilization of this software and other materials and
 * disclosure to any third parties is conditional upon having a separate
 * agreement with HERE for the access, use, utilization or disclosure of this
 * software. In the absence of such agreement, the use of the software is not
 * allowed.
 */

import {LuiBase, LuiPositioningMixin} from '@here/lui-base'
import MenuSpy from 'menuspy'
import jump from 'jump.js'
import scopedCss from 'lui-index.scss'
import template from 'lui-index.html'

const NAVIGATION_PANEL_HEIGHT = 70

export default class LuiIndex extends LuiPositioningMixin(LuiBase) {
  static get is() {
    return 'lui-index'
  }

  static get css() {
    return scopedCss
  }

  static get template() {
    return template
  }

  static get hasLuiElementsInShadowDom() {
    return false
  }

  init() {
    this._onClickBound = this._onClickBound || ((event) => this._onClick(event))
    const links = this.links
    if (links.length) {
      links.forEach((link) => {
        link.addEventListener('click', this._onClickBound)
        requestAnimationFrame(() => {
          if (!link.matches('[href^="#"]') &&
              link.href.replace(/#.*/, '') === window.location.href.replace(/#.*/, '')) {
            this.selectLink(link)
          }
          const nextElementSibling = link.nextElementSibling
          if (this.hasAttribute('auto-sections') &&
              nextElementSibling && nextElementSibling.tagName === 'LUI-INDEX') {
            this.removeChild(nextElementSibling)
          }
          if (link.getAttribute('aria-selected') === 'true') {
            link.setAttribute('tabindex', '-1')
            this.updateLinkHighlightPosition(link)
            this.initializeInPageLinks(link)
          }
        })
      })
    } else {
      this.initializeInPageLinks()
    }
    this.initializeMenuSpy()
  }

  initializeInPageLinks(link) {
    if (this.querySelector('lui-index')) return
    const selector = this.getAttribute('auto-sections')
    if (selector) {
      const elements = document.querySelectorAll(selector)
      if (elements.length) {
        const index = document.createElement('lui-index')
        elements.forEach((element) => {
          const sectionLink = document.createElement('a')
          if (element.id) {
            sectionLink.href = `#${element.id}`
            sectionLink.textContent = element.textContent
            index.appendChild(sectionLink)
          }
        })
        this.insertBefore(index, link ? link.nextSibling : null)
        index.initializeLinkHighlightPosition(index.firstChild)
        index.init()
        index.initializeMenuSpy()
      } else {
        this.initBound = this.initBound || (() => this.init())
        document.addEventListener('DOMContentLoaded', this.initBound)
      }
    }
  }

  initializeMenuSpy() {
    if (this._menuSpy) return
    if (this.inPageLinks) {
      this._menuSpy = new MenuSpy(this, {
        callback: (currentItem) => {
          this.selectLink(currentItem.elm)
        },
      })
    }
  }

  destroyMenuSpy() {
    if (this._menuSpy) {
      this._menuSpy.destroy()
      this._menuSpy = null
    }
  }

  onDisconnected() {
    this.links.forEach((link) => {
      link.removeEventListener('click', this._onClickBound)
    })
    if (this.initBound) {
      document.addEventListener('DOMContentLoaded', this.initBound)
    }
    this.destroyMenuSpy()
  }

  get links() {
    return Array.from(this.childNodes).filter((n) => n.tagName === 'A')
  }

  get selectedLinks() {
    return this.links.filter((n) => n.matches('[aria-selected="true"]'))
  }

  get inPageLinks() {
    return this.links.filter((n) => n.matches('[href^="#"]'))
  }

  _onClick(event) {
    const link = event.target
    if (link.matches('[aria-selected="true"]')) {
      event.preventDefault()
      return
    }
    this.selectLink(link)
    const href = link.getAttribute('href')
    if (href.match(/^#/)) {
      event.preventDefault()
      this.destroyMenuSpy()
      jump(href, {
        offset: -NAVIGATION_PANEL_HEIGHT,
        duration: (distanceInPx) => Math.sqrt(Math.abs(distanceInPx)) * 20,
        callback: () => { this.initializeMenuSpy() },
      })
    }
  }

  selectLink(link) {
    this.setLinkHighlight(link)
    this.selectedLinks.forEach((link) => {
      link.removeAttribute('aria-selected')
      link.removeAttribute('tabindex')
    })
    link.setAttribute('aria-selected', 'true')
    link.setAttribute('tabindex', '-1')
  }

  setLinkHighlight(link) {
    if (!link) return
    requestAnimationFrame(() => this.updateLinkHighlightPosition(link))
  }

  initializeLinkHighlightPosition(link) {
    const relativePosition = this.getRelativePosition(link)

    const rect = link.getBoundingClientRect()
    this.style.setProperty('--lui-index-selected-top', (relativePosition.top + rect.height / 2) + 'px')
    this.style.setProperty('--lui-index-selected-left', (relativePosition.left + rect.width / 2) + 'px')
    this.style.setProperty('--lui-index-selected-width', 0)
    this.style.setProperty('--lui-index-selected-height', 0)
  }

  updateLinkHighlightPosition(link) {
    if (this.links.length === 0) return

    const relativePosition = this.getRelativePosition(link)

    const rect = link.getBoundingClientRect()
    this.style.setProperty('--lui-index-selected-top', relativePosition.top + 'px')
    this.style.setProperty('--lui-index-selected-left', (relativePosition.left + 10) + 'px')
    this.style.setProperty('--lui-index-selected-width', (rect.width - 20) + 'px')
    this.style.setProperty('--lui-index-selected-height', rect.height + 'px')
  }
}



//////////////////
// WEBPACK FOOTER
// ./packages/lui-index/lui-index.js
// module id = 221
// module chunks = 0 1 16